import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { RegexTypeEnum } from '@app/shared/enums-interfaces/_internal';

/* eslint-disable @typescript-eslint/no-explicit-any */

enum FormSubmitResult {
    Invalid,
    Valid,
}

function onFormSubmit(form: FormGroup): FormSubmitResult {
    form.markAllAsTouched();

    if (form.invalid) {
        return FormSubmitResult.Invalid;
    }

    form.disable();
    return FormSubmitResult.Valid;
}

function onFormComplete(form: FormGroup) {
    form.enable();
}

function email(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const emailRegex = new RegExp(RegexTypeEnum.EMAIL_PATTERN);

        const isValid = emailRegex.test(control.value);

        return isValid ? null : { invalidEmail: true };
    };
}

function password(): ValidatorFn {
    return (control: AbstractControl): Record<string, any> | null => {
        const value = control.value;

        if (!value) {
            return null; // return if empty, as required validator should handle this case
        }

        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumber = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>+=\-[\];~`]/.test(value);
        const hasMinLength = value.length >= 8;

        const valid = hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && hasMinLength;

        return valid ? null : { passwordStrength: true };
    };
}

function name(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const regex = /^[A-Za-z'\s-]+$/;

        const isValid = regex.test(control.value);

        return isValid ? null : { invalidName: true };
    };
}

function match(controlName: string, matchingControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): Record<string, boolean> | null => {
        const control = formGroup.get(controlName);
        const matchingControl = formGroup.get(matchingControlName);

        if (!control || !matchingControl) {
            return null;
        }

        if (matchingControl.errors && !matchingControl.errors['mismatch']) {
            return null;
        }

        // set error on matchingControl if validation fails
        if (control.value !== matchingControl.value) {
            matchingControl.setErrors({ mismatch: true });
            return { mismatch: true };
        } else {
            matchingControl.setErrors(null);
            return null;
        }
    };
}

function emptyInput(inputValue: FormControl | AbstractControl): ValidationErrors | null {
    if (!inputValue.value) {
        return null;
    }
    return inputValue!.value.trim() !== '' ? null : { emptyInput: true };
}

function emptyStringOrObject(inputValue: FormControl | AbstractControl): ValidationErrors | null {
    if (typeof inputValue.value === 'string') {
        return inputValue!.value?.trim() !== '' ? null : { emptyInput: true };
    } else {
        return inputValue!.value?.value?.trim() !== '' ? null : { emptyInput: true };
    }
}

function dateRangeYear(minYear?: number, maxYear?: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const date = new Date(control.value);
        const year = date.getFullYear();

        // If minYear is provided, check if the date is before it
        if (minYear !== undefined && year < minYear) {
            return { minDate: { min: minYear, actual: year } };
        }

        // If maxYear is provided, check if the date is after it
        if (maxYear !== undefined && year > maxYear) {
            return { maxDate: { max: maxYear, actual: year } };
        }

        return null;
    };
}

function nullUndefined(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    return value === null || value === undefined ? { required: true } : null;
}

function quillRequired(control: AbstractControl): ValidationErrors | null {
    const quillText = control.value?.replace(/<(.|\n)*?>/g, '').trim();
    if (!quillText) {
        return { required: true };
    } else {
        return null;
    }
}

function happyFoxTicketId(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
        return null;
    }

    const value = control.value.toString().trim();

    if (value.length > 200) {
        return { maxlength: { invalidHappyFoxTicketId: true } };
    }

    const numericValue = value.replace(/\D/g, '');
    if (numericValue !== value || numericValue.length === 0) {
        return { invalidHappyFoxTicketId: true };
    }

    const number = Number(numericValue);
    if (number > 2147483647) {
        return { invalidHappyFoxTicketId: true };
    }

    return null;
}

export const FormHelper = {
    FormSubmitResult,
    onFormSubmit,
    onFormComplete,
};

export const FormValidators = {
    password,
    email,
    name,
    match,
    emptyInput,
    emptyStringOrObject,
    dateRangeYear,
    nullUndefined,
    quillRequired,
    happyFoxTicketId,
};
